import React, { Component } from 'react';
import getConfig from 'next/config';
import get from 'lodash/get';

import css from './Complete.module.scss';
import {
  buildCompleteHeading,
  buildConfirmationMessage,
  buildPricingMessage,
  buildChildrenHeading
} from './utils.ts';
import ChildComplete from './ChildComplete/index.tsx';
import Message from '../../components/UI/Message';
import AppDownloadLinks from './AppDownloadLinks.tsx';
import { getCompletePromoConfig } from '../../helpers/promotions.ts';
import CreditWaitlistModal from './CreditWaitlistModal/container.tsx';
import PlanFlowComplete from './PlanFlowComplete/index.tsx';
import CreditCompleteImage from './CreditCompleteImage';
import { SmsSubscriptionType } from '../../types/braze.types';
import { planPrices } from '../../utils/prices.ts';

const { publicRuntimeConfig } = getConfig();

class Complete extends Component {
  getSelectedPlanName(currentPlanName) {
    return (
      currentPlanName ||
      get(this, 'props.answers.setPlan.pricingPlanName') ||
      'standard'
    );
  }

  isMaxPlanFlow() {
    const selectedPlanName = this.getSelectedPlanName(
      this.props.currentPlanName
    );
    return (
      get(this, 'props.planFlow.enabled', false) &&
      get(this, 'props.planFlow.variables.planName') === 'max' &&
      selectedPlanName === 'max'
    );
  }

  componentDidMount() {
    window.scrollTo(0, 0);

    this.props.analytics.trackEvent('Feature Toggle Decided', {
      eventProps: {
        feature_key: 'acquisition_improvedDownloadScreen',
        variation_key: this.props.improvedDownloadScreen.variationKey,
        toggle_enabled: this.props.improvedDownloadScreen.enabled,
        experiment_key: 'acquisition_improvedDownloadScreen_test'
      }
    });

    /* eslint-disable no-undef */
    if (this.props.registrationToken) {
      this.props.queryGetUser();
      if (this.props.answers.setRole !== 'child')
        this.props.getFundingAccounts(this.props.registrationToken);
    }

    if (this.props.answers.setAutofundingPreference) {
      this.props.analytics.trackEvent('Registration Autofunding Toggle Set', {
        eventProps: {
          autofunding_toggle_on:
            this.props.answers.setAutofundingPreference.walletAutofundingEnabled
        }
      });
    }

    if (this.props.registeredUser && this.props.registeredUser.user) {
      const plans = planPrices({
        enableGSRChange: this.props.enableCorePriceChange,
        enableCorePriceChange: this.props.enableCorePriceChange
      });
      this.fireAnalytics(this.props.registeredUser.user, plans);
      this.subscribeToTransactionalSMS();
      this.firePostRegCompleteIfUserCompleted();
    }
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.registeredUser !== prevProps.registeredUser &&
      this.props.registeredUser &&
      this.props.registeredUser.user &&
      !get(prevProps, 'registeredUser.user')
    ) {
      const plans = planPrices({
        enableGSRChange: this.props.enableCorePriceChange,
        enableCorePriceChange: this.props.enableCorePriceChange
      });
      this.fireAnalytics(this.props.registeredUser.user, plans);
      this.subscribeToTransactionalSMS();
      this.firePostRegCompleteIfUserCompleted();
    }
  }

  subscribeToTransactionalSMS() {
    if (!this.props.completeUser) return;
    if (!this.props.registeredUser.user) return;
    this.props.subscribeSMS({
      userId: this.props.registeredUser.user.id,
      subscriptionType: SmsSubscriptionType.TRANSACTIONAL
    });
  }

  firePostRegCompleteIfUserCompleted() {
    if (this.props.completeUser) {
      this.props.postRegComplete();
    }
  }

  fireAnalytics(user, plans) {
    const { currentPlanName } = this.props;
    const selectedPlanName = this.getSelectedPlanName(currentPlanName);
    const fee = plans[selectedPlanName];

    const environment = get(publicRuntimeConfig, 'nodeEnv', 'dev');
    this.props.analytics.trackEvent('Registration Complete Viewed', {
      eventProps: {
        role: user.role
      }
    });
    if (user.role === 'parent') {
      if (environment === 'production' || environment === 'staging') {
        if (selectedPlanName !== 'standard') {
          this.props.analytics.trackEvent('Plan Selected', {
            eventProps: {
              plan: selectedPlanName,
              price: fee || undefined
            }
          });
        }
      }
    }
  }

  render() {
    const {
      flowId,
      appOs,
      promoId,
      nextRegStep,
      answers,
      registeredUser,
      error,
      upgradePlanError,
      currentPlanName,
      message,
      upgradePlanErrorMessage,
      needsVerification,
      partner,
      creditWaitlistEnabled,
      improvedDownloadScreen,
      enableCorePriceChange,
      enableGSRChange,
      riskFreePaintedDoorEnabled
    } = this.props;
    const isInApp = appOs !== null;
    const isMorganStanleyParentFlow = flowId === 'parent_flow_14';
    const isCreditFlow = flowId === 'parent_flow_16' || 'parent_flow_17';

    const plans = planPrices({ enableGSRChange, enableCorePriceChange });

    if (!registeredUser || !answers.setRole) return <div />;

    if (answers.setRole === 'child') {
      return <ChildComplete appOs={appOs} nextRegStep={nextRegStep} />;
    }

    const isError = error || upgradePlanError;
    const hasMultipleChildren = () =>
      get(registeredUser, 'children.length', 0) > 1;
    const selectedPlanName = this.getSelectedPlanName(currentPlanName);
    const promoConfig = getCompletePromoConfig(promoId);

    if (isInApp) {
      return <div />;
    }

    if (this.isMaxPlanFlow()) {
      return <PlanFlowComplete />;
    }

    return (
      <>
        <div className={css.CompleteMainBox} data-e2e="ddr-complete">
          {(isError || message) && (
            <Message
              error={isError}
              text={
                message ||
                upgradePlanErrorMessage ||
                'Please correct fields below'
              }
            />
          )}
          {answers.setRole === 'parent' && (
            <h1
              data-e2e="child-message"
              data-testid="child-message"
              className={isCreditFlow ? css.creditCompleteHeader : ''}
            >
              {buildCompleteHeading(
                registeredUser.children,
                promoId,
                answers,
                !!answers.creditApplication,
                improvedDownloadScreen.enabled
              )}
            </h1>
          )}
          {answers.creditApplication ? (
            <CreditCompleteImage
              creditApplication={answers.creditApplication}
            />
          ) : (
            <>
              {improvedDownloadScreen.enabled ? (
                <div className={css.improvedCompleteBody}>
                  <AppDownloadLinks promoId={promoId} partner={partner} />
                  <img
                    data-e2e="complete-image"
                    src={
                      promoConfig.image ||
                      `../../../static/download_page_example.png`
                    }
                    alt="Greenlight card example"
                  />
                </div>
              ) : (
                <div className={css.completeImage}>
                  <img
                    data-e2e="complete-image"
                    src={
                      promoConfig.image ||
                      `../../../static/complete-custom-white.png`
                    }
                    alt="Greenlight card example"
                  />
                </div>
              )}
            </>
          )}
          <div className={css.CompleteBody}>
            {!answers.creditApplication && (
              <h3 data-testid="complete-message">
                {!improvedDownloadScreen.enabled
                  ? 'Let’s finish setting up your account'
                  : buildChildrenHeading(registeredUser.children, promoId)}
              </h3>
            )}
            <p
              data-e2e="confirm-message"
              data-testid="confirm-message"
              style={{ marginBottom: '30px', whiteSpace: 'pre-wrap' }}
            >
              {buildConfirmationMessage(
                answers,
                registeredUser,
                needsVerification,
                !!answers.creditApplication
              )}
            </p>
            {!improvedDownloadScreen.enabled && (
              <div>
                <p className={css.appStoreText}>Install the app now:</p>
                <AppDownloadLinks promoId={promoId} partner={partner} />
              </div>
            )}
            {answers.setRole === 'parent' && (
              <p
                className={
                  improvedDownloadScreen
                    ? css.improvedBottomText
                    : css.bottomText
                }
                data-e2e="pricing-message"
                data-testid="pricing-message"
              >
                {buildPricingMessage({
                  hasMultipleChildren: hasMultipleChildren(),
                  plans,
                  promoId,
                  selectedPlanName,
                  answers,
                  riskFreePaintedDoorEnabled
                })}
              </p>
            )}
            {isMorganStanleyParentFlow && (
              <p
                className={css.cashPlusText}
                data-testid="ms-cash-plus-message"
              >
                Verify your CashPlus checking account to waive your $
                {plans.select}/mo fee.
              </p>
            )}
          </div>
        </div>
        {creditWaitlistEnabled && <CreditWaitlistModal />}
      </>
    );
  }
}

export default Complete;
